import React, { useEffect, useState } from 'react';
import { DemiLightText } from '../lib/Text';
import { formatAmountText, stripAmountComma } from '../lib/utils';
import { useAmountInputCaret } from '../lib/amountInputCaret';

/**
 * 금액·회차처럼 숫자를 받는 입력.
 *
 * 같은 컴포넌트가 파일마다 복사돼 있었고, 전부 같은 문제를 갖고 있었다.
 * 입력할 때마다 값을 통째로 재구성해서 **커서가 항상 끝으로 튀었다.**
 *   - 앞자리 0 을 입력 도중에 slice 로 잘라내 글자 수가 줄었다
 *   - 부모가 Number(value) 로 바꿔 되돌려줘 "05" 같은 중간 상태가 사라졌다
 * 그래서 가운데 글자를 고칠 수가 없었다.
 *
 * 여기서는 입력 중에 사용자가 친 문자열을 그대로 들고 있고, 부모에게는 숫자만 알린다.
 * 부모가 실제로 다른 수로 바꿨을 때만 동기화하고, "05" 같은 표기는 포커스가 빠질 때 정리한다.
 */
export type NumberTextInputProps = {
    value: string | number;
    onChange: (value: number) => void;
    /** 단위 (예: 원, 회, %) */
    unit?: string;
    placeholder?: string;
    /**
     * 원래 이 입력은 너비를 지정하지 않고 부모 폭에 맞춰 늘어났다.
     * prop 은 호출부 호환을 위해 남겨두되 기본은 늘어나는 동작을 유지한다.
     * 정말 고정하고 싶을 때만 fixedWidth 를 켠다.
     */
    width?: number;
    fixedWidth?: boolean;
    /** 입력 높이. 결제 이력 수정 모달은 67px 을 쓴다. */
    height?: number;
    disabled?: boolean;
    onBlur?: () => void;
    error?: boolean;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    /** 소수점 허용 (수수료율 등). 기본은 정수만. */
    allowDecimal?: boolean;
    /** 마운트 시 커서를 잡는다. 분할결제 모달은 열자마자 금액을 치던 흐름이 있었다. */
    autoFocus?: boolean;
    /**
     * 금액 표시용 3자리 콤마. **회차·비율에는 켜지 마라.**
     *
     * 화면에만 콤마를 붙이고 onChange 로 나가는 값은 언제나 콤마 없는 숫자다.
     * unit 이 '원' 인지로 자동 판별하지 않는다 — 표시 문자열로 켜면 회차·비율 입력에
     * 콤마가 새어 들어간다. 호출부에서 명시적으로 켜는 opt-in 이다.
     */
    comma?: boolean;
};

const NumberTextInput: React.FC<NumberTextInputProps> = ({
    value,
    onChange,
    unit = '',
    placeholder = '',
    width,
    fixedWidth = false,
    height = 46,
    disabled = false,
    onBlur,
    error = false,
    onKeyDown,
    allowDecimal = false,
    autoFocus = false,
    comma = false,
}) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [isFocused, setIsFocused] = useState(false);
    /**
     * 콤마와 소수점은 같이 못 쓴다. 소수점을 허용하면 3자리 콤마가 소수부까지 침범하고,
     * 콤마를 허용하면 소수점 입력이 AMOUNT_INPUT_PATTERN 에 걸려 통째로 거부된다.
     * 둘 다 켜면 **콤마를 무시**한다 — 콤마를 우선하면 수수료율 같은 소수 입력이
     * 조용히 막혀 값이 안 들어가는데, 그건 사용자가 알아채기 어렵다.
     */
    const commaMode = comma && !allowDecimal;
    const [text, setText] = useState(() =>
        commaMode ? formatAmountText(String(value ?? '')) : String(value ?? ''),
    );
    /**
     * 거부 규칙과 커서 복원은 금액 입력 전체가 같은 것을 써야 한다.
     * 훅은 조건부로 못 부르므로 콤마 모드가 아니어도 항상 부르고, 쓸 것만 골라 쓴다.
     * 붙여넣기·드롭 차단(pasteAmount/dropAmount)은 **콤마 모드에서만** 연결한다 —
     * 회차(`unit="회"`)·비율 입력의 붙여넣기 동작까지 바꾸면 안 된다.
     */
    const { changeAmount, dropPendingCaret, pasteAmount, dropAmount } = useAmountInputCaret();

    useEffect(() => {
        // 부모가 실제로 다른 수로 바꿨을 때만 동기화한다.
        // 같은 수를 다르게 적은 것(예: "05")까지 덮으면 입력 중에 커서가 튄다.
        setText((current) => {
            if (commaMode) {
                // Number("1,000") 은 NaN 이다. 콤마를 걷어내고 비교하지 않으면
                // 부모가 값을 돌려주는 즉시 콤마가 사라진다.
                if (Number(stripAmountComma(current)) === Number(value)) return current;
                // 부모·서버가 값을 갈아끼웠다(rollback, 다른 상품 선택 등).
                // 사용자가 치던 자리의 낡은 커서 복원은 버린다.
                // ref 를 비우기만 하므로 updater 가 두 번 불려도 결과가 같다.
                dropPendingCaret();
                return formatAmountText(String(value ?? ''));
            }
            return Number(current) === Number(value) ? current : String(value ?? '');
        });
    }, [value, commaMode, dropPendingCaret]);

    useEffect(() => {
        if (autoFocus) inputRef.current?.focus();
        // 마운트 시 한 번만. 이후 prop 변화로 커서를 뺏지 않는다.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pattern = allowDecimal ? /^\d*\.?\d*$/ : /^\d*$/;

    return (
        <div
            // 기본은 부모 폭에 맞춰 늘어난다(원래 동작). fixedWidth 를 켤 때만 고정한다.
            style={{ height, ...(fixedWidth && width ? { width } : {}) }}
            className={`flex items-center border border-solid rounded-[10px] px-[18px] cursor-text hover:border-primary transition-colors duration-300 ease-in-out ${
                disabled ? 'bg-gray-100 text-gray-400' : 'bg-white'
            } ${isFocused ? 'border-primary' : error ? 'border-red' : 'border-[#D9D9D9B2]'}`}
            onClick={() => inputRef.current?.focus()}
        >
            <input
                ref={inputRef}
                type="text"
                inputMode={allowDecimal ? 'decimal' : 'numeric'}
                value={text}
                onChange={(e) => {
                    const raw = e.target.value;
                    if (commaMode) {
                        // 거부(null)면 값을 아예 건드리지 않는다 — 걷어내는 방식(replace)은
                        // 실패 대신 다른 유효 금액을 만든다 ("-100"→100, "12.34"→1234).
                        // 커서 기억도 훅이 한다(자릿수가 그대로면 아예 기억하지 않는다).
                        const digits = changeAmount(e, stripAmountComma(text));
                        if (digits === null) return;
                        setText(formatAmountText(digits));
                        // 부모에게는 콤마 없는 숫자만 넘긴다. parseInt('1,000') 은 1 이다.
                        onChange(Number(digits) || 0);
                        return;
                    }
                    // 숫자가 아니면 조용히 무시한다. 값을 바꾸지 않으므로 커서도 그대로다.
                    if (!pattern.test(raw)) return;
                    setText(raw);
                    onChange(raw === '' || raw === '.' ? 0 : Number(raw));
                }}
                onFocus={() => setIsFocused(true)}
                onBlur={() => {
                    setIsFocused(false);
                    // 입력이 끝나면 "05" 같은 표기를 정리한다. 입력 중에 지우면 커서가 튄다.
                    setText(
                        commaMode
                            ? formatAmountText(String(Number(stripAmountComma(text)) || 0))
                            : String(Number(text) || 0),
                    );
                    onBlur?.();
                }}
                onKeyDown={onKeyDown}
                /*
                 * 붙여넣기·드롭은 한 번에 값이 확정되므로 타이핑보다 엄격하게 본다.
                 * `10000,20000`(엑셀 CSV 조각)이 통과하면 콤마를 걷어낸 `1,000,020,000`
                 * 이라는 전혀 다른 금액이 만들어진다. 콤마 모드가 아닐 때는 아예 붙이지 않는다.
                 */
                onPaste={commaMode ? pasteAmount : undefined}
                onDrop={commaMode ? dropAmount : undefined}
                placeholder={placeholder}
                disabled={disabled}
                className={`flex flex-grow outline-none w-[80px] text-[16px] leading-[23px] text-[#3D4244] bg-transparent ${
                    disabled ? 'cursor-not-allowed' : ''
                }`}
            />
            {unit && <DemiLightText className="flex">{unit}</DemiLightText>}
        </div>
    );
};

export default NumberTextInput;
